// src/pages/Warehouse/Incoming/AddIncomingItems.jsx
// UPDATED VERSION WITH BOTH PRICE FIELDS
import React, { useEffect, useMemo, useState } from "react"
import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tooltip,
  Upload,
  message,
} from "antd"
import { PlusOutlined, UploadOutlined, MinusCircleOutlined, ClearOutlined } from "@ant-design/icons"
import dayjs from "dayjs"
import { addDoc, collection, doc, getDoc, onSnapshot, serverTimestamp } from "firebase/firestore"
import { ref, uploadBytes, getDownloadURL } from "firebase/storage"
import { db, storage } from "../../../../firebase/config"
import {
  logIncomingEvent,
  logInventoryCreatedEvent,
  logRollCreatedEvent,
} from "../Helper/warehouseEvents"

const UNIT_OPTIONS_DEFAULT = [
  { label: "kg", value: "kg" },
  { label: "m²", value: "m2" },
  { label: "m", value: "m" },
  { label: "pcs", value: "pcs" },
  { label: "box", value: "box" },
]

const FIELD_LAYOUT = {
  default: {
    order: [
      "productName",
      "material",
      "width",
      "unit",
      "amount",
      "originalMeters",
      "price",
      "total",
      "ean",
      "barcode",
      "series",
      "manufacturerCode",
      "batchSeries",
    ],
    hide: [],
    widths: {
      productName: 260,
      material: 180,
      width: 120,
      materialWidth: 120,
      unit: 120,
      amount: 120,
      originalMeters: 140,
      price: 120,
      total: 140,
      ean: 160,
      barcode: 160,
      series: 140,
      manufacturerCode: 180,
      batchSeries: 160,
      notes: 240,
    },
    labels: {
      productName: "Product name",
      amount: "Quantity",
      originalMeters: "Meters",
      manufacturerCode: "Manufacturer prod.",
      batchSeries: "Batch series",
      total: "Total",
      ean: "EAN",
      width: "Width",
      materialWidth: "Width",
    },
    align: {
      amount: "right",
      price: "right",
      total: "right",
      width: "right",
      materialWidth: "right",
      originalMeters: "right",
    },
    typeHints: {
      material: "select",
      width: "number",
      materialWidth: "number",
      unit: "select",
      amount: "number",
      originalMeters: "number",
      price: "number",
      total: "number",
      ean: "text",
      barcode: "text",
      series: "text",
      manufacturerCode: "text",
      batchSeries: "text",
    },
  },
  byTypeId: {},
}

const makeBlankRow = () => ({
  productName: "",
  unit: undefined,
  amount: undefined,
  price: undefined,
  total: undefined,
  notes: "",
})

const ROLL_TYPE_HINTS = ["roll"]
const isRollType = (productTypeName) => {
  if (!productTypeName) return false
  const name = String(productTypeName).toLowerCase()
  return ROLL_TYPE_HINTS.some((t) => name.includes(t))
}

const DUPLICATE_ROLLS_TO_INVENTORY = false

export const buildInventoryDoc = ({ row, productTypeName, materialName, meta = {} }) => {
  const quantityRaw = row?.amount ?? row?.qty ?? row?.quantity
  const quantity = Number.isFinite(Number(quantityRaw)) ? Number(quantityRaw) : 0

  return {
    productTypeName: productTypeName || null,
    productName: row?.productName || null,
    unit: row?.unit || null,
    quantity,
    materialName: materialName || null,
    material: materialName || null,
    barcode: row?.barcode || null,
    ean: row?.ean ?? null,
    batchSeries: row?.batchSeries ?? null,
    pricePerUnit: Number.isFinite(Number(row?.price)) ? Number(row.price) : null,
    totalPrice: Number.isFinite(Number(row?.total)) ? Number(row.total) : null,
    location: "Warehouse",
    notes: row?.notes || null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    arrivedAt: meta.arrivedAt ?? null,
    supplierId: meta.supplierId ?? null,
    supplierName: meta.supplierName ?? null,
    productTypeId: meta.productTypeId ?? null,
    materialId: meta.materialId ?? row?.material ?? null,
  }
}

export const buildInventoryDocFromRoll = ({ rollDoc, meta = {} }) => {
  const meters = Number(rollDoc?.remainingMeters ?? rollDoc?.originalMeters)
  const weight = Number(rollDoc?.weight)
  let unit = "pcs"
  let quantity = 1
  if (Number.isFinite(meters) && meters > 0) {
    unit = "m"
    quantity = meters
  } else if (Number.isFinite(weight) && weight > 0) {
    unit = "kg"
    quantity = weight
  }

  const materialName =
    rollDoc?.material?.material ?? rollDoc?.material?.supplierMaterialName ?? null

  return {
    productTypeName: rollDoc?.productTypeName || "Roll",
    productName: rollDoc?.productName || rollDoc?.barcode || "Roll",
    unit,
    quantity,
    materialName,
    material: materialName,
    barcode: rollDoc?.barcode || null,
    ean: rollDoc?.ean ?? rollDoc?.material?.EAN ?? null,
    batchSeries: rollDoc?.batchSeries ?? null,
    pricePerUnit: Number.isFinite(Number(rollDoc?.pricePerUnit))
      ? Number(rollDoc.pricePerUnit)
      : null,
    totalPrice: Number.isFinite(Number(rollDoc?.totalPrice)) ? Number(rollDoc.totalPrice) : null,
    location: rollDoc?.rollLocation || (rollDoc?.inStock ? "Warehouse" : null),
    notes: rollDoc?.comment || null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    arrivedAt: meta.arrivedAt ?? rollDoc?.receivedInStock ?? null,
    supplierId: meta.supplierId ?? null,
    supplierName: meta.supplierName ?? null,
    productTypeId: meta.productTypeId ?? null,
    materialId: meta.materialId ?? rollDoc?.materialId ?? null,
  }
}

export const buildRollDoc = ({
  row,
  arrivedAt,
  productTypeName,
  materialDoc,
  orderOverrides = null,
}) => {
  const unit = String(row?.unit || "").toLowerCase()
  const qty = Number(row?.amount)
  const widthMm = Number(row?.width ?? row?.materialWidth)
  let weightKg = Number(row?.weight)
  const gsm = Number(materialDoc?.gM2 ?? materialDoc?.gsm)

  let originalMeters = Number(row?.originalMeters)
  if (!Number.isFinite(originalMeters) || originalMeters <= 0) {
    if (unit === "m" && Number.isFinite(qty) && qty > 0) {
      originalMeters = qty
    } else if (
      Number.isFinite(weightKg) &&
      Number.isFinite(widthMm) &&
      Number.isFinite(gsm) &&
      widthMm > 0 &&
      gsm > 0
    ) {
      originalMeters = (weightKg * 1_000_000) / (gsm * widthMm)
    } else {
      originalMeters = null
    }
  }
  const remainingMeters = Number.isFinite(originalMeters) ? originalMeters : null

  if (
    (!Number.isFinite(weightKg) || weightKg <= 0) &&
    Number.isFinite(gsm) &&
    Number.isFinite(widthMm) &&
    Number.isFinite(originalMeters)
  ) {
    weightKg = (gsm * widthMm * originalMeters) / 1_000_000
  }
  if (!Number.isFinite(weightKg) || weightKg < 0) weightKg = 0

  const materialId = row?.material ?? null
  const material = materialDoc
    ? {
        id: materialDoc.id || materialId,
        material: materialDoc.material || row?.materialName || null,
        EAN: materialDoc.EAN ?? materialDoc.ean ?? null,

        eurM2: materialDoc.eurM2 ?? null,
        gM2: materialDoc.gM2 ?? materialDoc.gsm ?? null,
        materialFor: materialDoc.materialFor ?? null,
        stock: materialDoc.stock ?? null,
        comment: materialDoc.comment ?? null,
        supplier: materialDoc.supplier ?? null,
        widths: materialDoc.widths ?? null,
        slogan: materialDoc.slogan ?? null,
        supplierMaterialName: materialDoc.supplierMaterialName ?? null,
      }
    : materialId || row?.materialName
    ? { id: materialId, material: row?.materialName || null }
    : null

  const widthM = Number.isFinite(widthMm) ? widthMm / 1000 : null

  // Calculate pricePerUnit from form or eurM2
  let pricePerUnit = Number.isFinite(Number(row?.price)) ? Number(row.price) : null
  if (
    (pricePerUnit == null || !Number.isFinite(pricePerUnit)) &&
    Number.isFinite(widthM) &&
    Number.isFinite(material?.eurM2)
  ) {
    pricePerUnit = widthM * Number(material.eurM2) // price per meter
  }

  // Calculate totalPrice from form or pricePerUnit * meters
  let totalPrice = Number.isFinite(Number(row?.total)) ? Number(row.total) : null
  if (
    (totalPrice == null || !Number.isFinite(totalPrice)) &&
    Number.isFinite(pricePerUnit) &&
    Number.isFinite(originalMeters)
  ) {
    totalPrice = pricePerUnit * originalMeters
  }

  const ean = row?.ean ?? material?.EAN ?? null

  const base = {
    status: "InStock",
    inStock: true,
    inUse: false,
    inOrder: false,
    shipping: false,
    verified: false,
    productName: row?.productName || row?.barcode || "Roll",
    created: serverTimestamp(),
    updated: serverTimestamp(),
    receivedInStock: arrivedAt ? arrivedAt.toDate() : null,
    barcode: row?.barcode || null,
    ean,
    fullOrderNumber: null,
    orderNumber: null,
    orderId: null,
    orderIds: [],
    machine: row?.machine ?? null,
    destinationMachine: row?.destinationMachine ?? null,
    rollLocation: row?.rollLocation ?? "Warehouse",
    core: row?.core ?? 76,
    palletLocation: row?.palletLocation || "",
    pallet: row?.pallet || "",
    rollWidth: Number.isFinite(widthMm) ? widthMm : null,
    weight: weightKg,
    originalMeters,
    remainingMeters,
    pricePerUnit: Number.isFinite(pricePerUnit) ? pricePerUnit : null,
    totalPrice: Number.isFinite(totalPrice) ? totalPrice : null,
    // Keep old 'price' field for backwards compatibility
    price: Number.isFinite(totalPrice) ? totalPrice : null,
    materialId,
    material,
    comment: row?.notes || null,
    productTypeName: productTypeName || null,
  }

  if (orderOverrides && typeof orderOverrides === "object") {
    Object.assign(base, orderOverrides)
  }
  return base
}

export default function AddIncomingItemsModal({ currentUserId = null }) {
  const [open, setOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form] = Form.useForm()

  const [suppliers, setSuppliers] = useState([])
  const [productTypes, setProductTypes] = useState([])
  const [materials, setMaterials] = useState([])

  const [selectedTypeId, setSelectedTypeId] = useState()
  const [selectedType, setSelectedType] = useState(null)
  const [arrivedAt, setArrivedAt] = useState(dayjs())

  const [batchFileList, setBatchFileList] = useState([])

  useEffect(() => {
    const refCol = collection(db, "suppliers")
    return onSnapshot(refCol, (snap) => {
      setSuppliers(
        snap.docs.map((d) => {
          const s = d.data()
          const label = s.company || s.name || s.email || d.id

          return { label, value: d.id }
        })
      )
    })
  }, [])

  useEffect(() => {
    const refCol = collection(db, "warehouseProductTypes")
    return onSnapshot(refCol, (snap) => {
      setProductTypes(
        snap.docs.map((d) => {
          const s = d.data()
          return { label: s.name || d.id, value: d.id }
        })
      )
    })
  }, [])

  useEffect(() => {
    const refCol = collection(db, "materials")
    return onSnapshot(refCol, (snap) => {
      setMaterials(
        snap.docs.map((d) => {
          const m = d.data()
          return { label: m.material, value: d.id }
        })
      )
    })
  }, [])

  useEffect(() => {
    if (!selectedTypeId) {
      setSelectedType(null)
      return
    }
    ;(async () => {
      const typeRef = doc(db, "warehouseProductTypes", selectedTypeId)
      const ds = await getDoc(typeRef)
      setSelectedType(ds.exists() ? { id: ds.id, ...ds.data() } : null)
    })()
  }, [selectedTypeId])

  const mergedFieldSpecs = useMemo(() => {
    const KEY_ALIASES = { width: ["width", "materialWidth"] }
    const resolveKey = (k, map) => (KEY_ALIASES[k] || [k]).find((key) => map.has(key)) || null

    const base = [
      { key: "productName", label: "Product name", type: "text", required: true },
      { key: "unit", label: "Unit", type: "select", required: true, options: UNIT_OPTIONS_DEFAULT },
      { key: "amount", label: "Amount", type: "number" },
      { key: "price", label: "Price/unit", type: "number" },
      { key: "total", label: "Total", type: "number", required: true },
      { key: "notes", label: "Notes", type: "text" },
    ]
    const map = new Map(base.map((f) => [f.key, f]))

    for (const f of selectedType?.fields || []) {
      if (f.key === "unit") {
        map.set("unit", { ...map.get("unit"), ...f, options: f.options || UNIT_OPTIONS_DEFAULT })
        continue
      }
      if (f.key === "material") {
        map.set("material", {
          key: "material",
          label: f.label || "Material",
          type: "select",
          required: !!f.required,
        })
        continue
      }
      map.set(f.key, { ...(map.get(f.key) || {}), ...f })
    }

    const layout =
      (selectedType?.id && FIELD_LAYOUT.byTypeId?.[selectedType.id]) || FIELD_LAYOUT.default

    for (const k of layout.hide || []) map.delete(k)

    const remaining = new Map(map)
    const out = []
    let hadRest = false

    for (const token of layout.order || []) {
      if (token === "::rest") {
        hadRest = true
        const rest = Array.from(remaining.values()).sort((a, b) =>
          String(a.label || a.key).localeCompare(String(b.label || b.key))
        )
        out.push(...rest)
        remaining.clear()
      } else {
        const resolved = resolveKey(token, remaining)
        if (resolved) {
          const field = remaining.get(resolved)
          if (field.key === "materialWidth") field.label = "Width"
          out.push(field)
          remaining.delete(resolved)
        }
      }
    }

    if (hadRest && remaining.size) out.push(...remaining.values())

    for (const f of out) {
      if (layout.labels?.[f.key]) f.label = layout.labels[f.key]
      f.__width =
        FIELD_LAYOUT.default.widths?.[f.key] ??
        (f.type === "number"
          ? 120
          : f.type === "select"
          ? 160
          : f.key === "productName"
          ? 220
          : 160)
      f.__align = FIELD_LAYOUT.default.align?.[f.key] || (f.type === "number" ? "right" : undefined)
    }

    const seen = new Set()
    return out.filter((f) => (seen.has(f.key) ? false : (seen.add(f.key), true)))
  }, [selectedType])

  useEffect(() => {
    if (!selectedTypeId) {
      setSelectedType(null)
      return
    }
    ;(async () => {
      const typeRef = doc(db, "warehouseProductTypes", selectedTypeId)
      const ds = await getDoc(typeRef)
      const next = ds.exists() ? { id: ds.id, ...ds.data() } : null
      setSelectedType(next)
      form.setFieldsValue({ rows: [makeBlankRow()] })
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTypeId])

  const renderCell = (field, formField) => {
    const rules = field.required ? [{ required: true, message: "Required" }] : []
    if (field.type === "select") {
      const opts =
        field.key === "material"
          ? materials
          : field.key === "unit"
          ? field.options || UNIT_OPTIONS_DEFAULT
          : field.options || []
      return (
        <Form.Item name={[formField.name, field.key]} style={{ margin: 0 }} rules={rules}>
          <Select
            size="small"
            options={opts}
            placeholder={field.label}
            showSearch
            optionFilterProp="label"
          />
        </Form.Item>
      )
    }
    if (field.type === "number") {
      return (
        <Form.Item name={[formField.name, field.key]} style={{ margin: 0 }} rules={rules}>
          <InputNumber size="small" placeholder={field.label} style={{ width: "100%" }} />
        </Form.Item>
      )
    }
    return (
      <Form.Item name={[formField.name, field.key]} style={{ margin: 0 }} rules={rules}>
        <Input size="small" placeholder={field.label} />
      </Form.Item>
    )
  }

  const uploadBatchFileIfAny = async () => {
    if (!batchFileList.length) return null
    const file = batchFileList[0].originFileObj || batchFileList[0]
    const safeName = `${Date.now()}-${file.name}`.replace(/\s+/g, "_")
    const path = `warehouseIncoming/invoices/${safeName}`
    const storageRef = ref(storage, path)
    await uploadBytes(storageRef, file)
    const url = await getDownloadURL(storageRef)
    return {
      invoiceUrl: url,
      invoiceFileName: file.name,
      invoiceSize: file.size,
      invoiceType: file.type,
      invoiceStoragePath: path,
    }
  }

  const handleSaveAll = async () => {
    try {
      const top = await form.validateFields(["supplierId", "productTypeId", "arrivedAt"])
      const { rows = [] } = await form.validateFields(["rows"])
      if (!rows.length) {
        message.warning("Please add at least one row.")
        return
      }

      setSaving(true)

      const supplierOption = suppliers.find((s) => s.value === top.supplierId)
      const ptOption = productTypes.find((t) => t.value === top.productTypeId)
      const productTypeName = ptOption?.label || ""
      const invoiceMeta = await uploadBatchFileIfAny()

      if (isRollType(productTypeName)) {
        for (let i = 0; i < rows.length; i++) {
          const row = rows[i] || {}
          const om = Number(row.originalMeters)
          const unit = String(row.unit || "").toLowerCase()
          const amt = Number(row.amount)
          const hasMeters = Number.isFinite(om) && om > 0
          const hasMetersViaAmount = unit === "m" && Number.isFinite(amt) && amt > 0
          if (!hasMeters && !hasMetersViaAmount) {
            message.error(`Row ${i + 1}: please fill "Meters" or set Unit to "m" and enter Amount.`)
            setSaving(false)
            return
          }
        }
      }

      for (const row of rows) {
        const incoming = {
          createdAt: serverTimestamp(),
          arrivedAt: top.arrivedAt ? top.arrivedAt.toDate() : null,
          supplierId: top.supplierId,
          supplierName: supplierOption?.label || null,
          productTypeId: top.productTypeId,
          productTypeName,
        }

        for (const f of mergedFieldSpecs) {
          if (row[f.key] !== undefined) {
            incoming[f.key] = typeof row[f.key] === "string" ? row[f.key].trim() : row[f.key]
          }
        }

        if (isRollType(productTypeName)) {
          const om = Number(incoming.originalMeters)
          const unit = String(incoming.unit || "").toLowerCase()
          const amt = Number(incoming.amount)
          if (
            (!Number.isFinite(om) || om <= 0) &&
            unit === "m" &&
            Number.isFinite(amt) &&
            amt > 0
          ) {
            incoming.originalMeters = amt
          }
        }

        if (row.material != null) {
          const mo = materials.find((m) => m.value === row.material || m.label === row.material)
          incoming.materialName = mo?.label || null
        }

        if (invoiceMeta) Object.assign(incoming, invoiceMeta)

        const incomingRef = await addDoc(collection(db, "warehouseIncoming"), incoming)

        await logIncomingEvent({
          row: {
            ...row,
            originalMeters: incoming.originalMeters,
            materialName: incoming.materialName,
          },
          arrivedAt: top.arrivedAt ? top.arrivedAt.toDate() : new Date(),
          productTypeId: top.productTypeId,
          productTypeName,
          supplierId: top.supplierId,
          supplierName: supplierOption?.label || null,
          materialName: incoming.materialName,
          sourceDocId: incomingRef.id,
          userId: currentUserId,
        })

        if (isRollType(productTypeName)) {
          let materialDoc = null
          if (row.material) {
            try {
              const snap = await getDoc(doc(db, "materials", row.material))
              if (snap.exists()) materialDoc = { id: snap.id, ...snap.data() }
            } catch {
              /* ignore */
            }
          }

          const rollDoc = buildRollDoc({
            row: {
              ...row,
              originalMeters: incoming.originalMeters,
              materialName: incoming.materialName,
            },
            arrivedAt: top.arrivedAt,
            productTypeName,
            materialDoc,
          })

          const rollRef = await addDoc(collection(db, "rolls"), rollDoc)

          await logRollCreatedEvent({
            rollDoc,
            rollDocId: rollRef.id,
            arrivedAt: top.arrivedAt ? top.arrivedAt.toDate() : new Date(),
            userId: currentUserId,
          })

          if (DUPLICATE_ROLLS_TO_INVENTORY) {
            const invFromRoll = buildInventoryDocFromRoll({
              rollDoc,
              meta: {
                arrivedAt: incoming.arrivedAt,
                supplierId: incoming.supplierId,
                supplierName: incoming.supplierName,
                productTypeId: incoming.productTypeId,
                materialId: incoming.material || incoming.materialId || null,
              },
            })
            const invRef = await addDoc(collection(db, "warehouseInventory"), invFromRoll)

            await logInventoryCreatedEvent({
              inventoryDoc: invFromRoll,
              inventoryDocId: invRef.id,
              arrivedAt: top.arrivedAt ? top.arrivedAt.toDate() : new Date(),
              userId: currentUserId,
            })
          }
        } else {
          const invDoc = buildInventoryDoc({
            row,
            productTypeName,
            materialName: incoming.materialName || null,
            meta: {
              arrivedAt: incoming.arrivedAt,
              supplierId: incoming.supplierId,
              supplierName: incoming.supplierName,
              productTypeId: incoming.productTypeId,
              materialId: incoming.material || incoming.materialId || null,
            },
          })
          const invRef = await addDoc(collection(db, "warehouseInventory"), invDoc)

          await logInventoryCreatedEvent({
            inventoryDoc: invDoc,
            inventoryDocId: invRef.id,
            arrivedAt: top.arrivedAt ? top.arrivedAt.toDate() : new Date(),
            userId: currentUserId,
          })
        }
      }

      message.success(`Added ${rows.length} item(s)`)

      const keep = form.getFieldsValue(["supplierId", "productTypeId"])
      const today = dayjs()
      setArrivedAt(today)
      form.resetFields()
      form.setFieldsValue({
        supplierId: keep.supplierId,
        productTypeId: keep.productTypeId,
        arrivedAt: today,
        rows: [makeBlankRow()],
      })
      setBatchFileList([])
      setOpen(false)
    } catch (e) {
      if (e?.errorFields) return
      console.error(e)
      message.error("Failed to add items")
    } finally {
      setSaving(false)
    }
  }

  const handleOpen = () => {
    setOpen(true)
    const cur = form.getFieldsValue()
    if (!cur.rows || !cur.rows.length) {
      form.setFieldsValue({ arrivedAt, rows: [makeBlankRow()] })
    }
  }

  return (
    <>
      <Button type="primary" icon={<PlusOutlined />} onClick={handleOpen}>
        Add incoming
      </Button>

      <Modal
        title="Add incoming items"
        open={open}
        onCancel={() => setOpen(false)}
        width={1600}
        destroyOnClose={false}
        footer={
          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="primary" onClick={handleSaveAll} loading={saving}>
              Save all
            </Button>
          </Space>
        }>
        <Form
          form={form}
          layout="vertical"
          style={{ width: "100%" }}
          initialValues={{ arrivedAt, rows: [makeBlankRow()] }}>
          <Space size="large" style={{ display: "flex", marginBottom: 12 }} wrap>
            <Form.Item
              label="Supplier"
              name="supplierId"
              rules={[{ required: true, message: "Select supplier" }]}
              style={{ minWidth: 260 }}>
              <Select
                options={suppliers}
                placeholder="Select supplier"
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>

            <Form.Item
              label="Product type"
              name="productTypeId"
              rules={[{ required: true, message: "Select product type" }]}
              style={{ minWidth: 260 }}>
              <Select
                options={productTypes}
                placeholder="Select type"
                showSearch
                optionFilterProp="label"
                onChange={setSelectedTypeId}
              />
            </Form.Item>

            <Form.Item label="Arrival" name="arrivedAt" style={{ minWidth: 180 }}>
              <DatePicker
                allowClear={false}
                format="DD.MM.YYYY"
                value={arrivedAt}
                onChange={(v) => {
                  setArrivedAt(v)
                  form.setFieldValue("arrivedAt", v)
                }}
              />
            </Form.Item>

            <Form.Item label="Invoice / attachment" style={{ minWidth: 280 }}>
              <Upload
                fileList={batchFileList}
                beforeUpload={(file) => {
                  setBatchFileList([file])
                  return false
                }}
                onRemove={() => setBatchFileList([])}
                maxCount={1}
                accept=".pdf,image/*,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx">
                <Button icon={<UploadOutlined />}>Select file (applies to all rows)</Button>
              </Upload>
            </Form.Item>
          </Space>

          <Form.List name="rows">
            {(fields, { add, remove }) => {
              const cols = [
                ...mergedFieldSpecs.map((spec) => ({
                  title: spec.label || spec.key,
                  dataIndex: spec.key,
                  key: spec.key,
                  width: spec.__width,
                  align: spec.__align,
                  render: (_, fieldMeta) => renderCell(spec, fieldMeta),
                })),
                {
                  title: "",
                  dataIndex: "actions",
                  key: "actions",
                  width: 48,
                  fixed: "right",
                  render: (_, fieldMeta) => (
                    <Tooltip title="Remove row">
                      <Button
                        icon={<MinusCircleOutlined />}
                        type="text"
                        danger
                        onClick={() => remove(fieldMeta.name)}
                      />
                    </Tooltip>
                  ),
                },
              ]

              return (
                <>
                  <Space style={{ marginBottom: 8 }}>
                    <Tooltip title="Add a new empty row">
                      <Button icon={<PlusOutlined />} onClick={() => add(makeBlankRow())}>
                        Add row
                      </Button>
                    </Tooltip>
                    <Tooltip title="Clear all rows (leave one blank)">
                      <Button
                        icon={<ClearOutlined />}
                        onClick={() => {
                          for (let i = fields.length - 1; i >= 0; i--) remove(i)
                          add(makeBlankRow())
                        }}>
                        Clear rows
                      </Button>
                    </Tooltip>
                  </Space>

                  <Table
                    dataSource={fields}
                    rowKey={(f) => f.key}
                    pagination={false}
                    size="small"
                    bordered
                    columns={cols}
                  />
                </>
              )
            }}
          </Form.List>
        </Form>
      </Modal>
    </>
  )
}
